// src/components/KnowledgeBasePage.js
import React, { useState, useEffect } from 'react';
import axiosInstance from '../api/axiosInstance';
import { motion } from 'framer-motion';
import PageHeader from './common/PageHeader';

// ✨ NEW: استيراد مكونات إضافية
import { Container, Typography, Box, CircularProgress, Alert, Paper, TextField, Button, Snackbar, Chip, Divider } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SaveIcon from '@mui/icons-material/Save';
import DescriptionIcon from '@mui/icons-material/Description';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

const pageVariants = {
  initial: { opacity: 0, x: "-5vw" },
  in: { opacity: 1, x: 0 },
  out: { opacity: 0, x: "5vw" }
};
const pageTransition = {
  type: "tween",
  ease: "anticipate",
  duration: 0.5
};

export default function KnowledgeBasePage() {
  // ✨ UPDATED: استخدام حالة للبيانات قابلة للتعديل
  const [formData, setFormData] = useState({
    ai_prompt: '',
    knowledge_base_text: '',
  });
  const [currentFileName, setCurrentFileName] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  
  // ✨ NEW: حالة لإظهار رسالة النجاح
  const [snackbarOpen, setSnackbarOpen] = useState(false);


  useEffect(() => {
    const fetchKnowledgeBase = async () => {
      try {
        const response = await axiosInstance.get(`/chatbot/knowledge-base/`);
        // تعبئة الحقول بالبيانات القادمة من السيرفر
        setFormData({
          ai_prompt: response.data.ai_prompt || '',
          knowledge_base_text: response.data.knowledge_base_text || '',
        });
        setCurrentFileName(response.data.knowledge_base_file ? response.data.knowledge_base_file.split('/').pop() : '');
        console.log('✅ Knowledge base loaded successfully');
      } catch (err) {
        console.log('⚠️ Knowledge base endpoint not available, using empty form');
        // Use empty form data as fallback
        setFormData({
          ai_prompt: '',
          knowledge_base_text: '',
        });
        setCurrentFileName('');
        if (err.response?.status !== 401) {
          console.log('Knowledge base not found, starting with empty form');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchKnowledgeBase();
  }, []);

  // ✨ NEW: دوال للتعامل مع التغييرات في الحقول والملف
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  // ✨ NEW: دالة لإرسال التحديثات للسيرفر
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    // FormData ضروري لإرسال الملفات مع البيانات النصية
    const dataToSubmit = new FormData();
    dataToSubmit.append('ai_prompt', formData.ai_prompt);
    dataToSubmit.append('knowledge_base_text', formData.knowledge_base_text);
    if (selectedFile) {
      dataToSubmit.append('knowledge_base_file', selectedFile);
    }

    try {
      await axiosInstance.put(`/chatbot/knowledge-base/`, dataToSubmit, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setSnackbarOpen(true); // إظهار رسالة النجاح
      if (selectedFile) {
        setCurrentFileName(selectedFile.name);
        setSelectedFile(null);
      }
    } catch (err) {
      setError('فشل حفظ التغييرات. يرجى المحاولة مرة أخرى.');
    } finally {
      setSaving(false);
    }
  };


  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        gap: 3
      }}>
        <CircularProgress size={60} thickness={4} sx={{ color: '#3b82f6' }} />
        <Typography variant="h6" sx={{ fontWeight: 500, color: '#64748b' }}>
          جاري تحميل قاعدة المعرفة...
        </Typography>
      </Box>
    );
  }

  return (
    <motion.div initial="initial" animate="in" exit="out" variants={pageVariants} transition={pageTransition}>
      <Container maxWidth="lg">
        <PageHeader
          title="إدارة قاعدة المعرفة"
          subtitle="إعداد وتحديث المعلومات والملفات التي يستخدمها الشات بوت"
          variant="gradient"
        />
        
        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 2,
              borderRadius: 3,
              '& .MuiAlert-message': {
                fontSize: '1rem',
                fontWeight: 500
              }
            }}
          >
            {error}
          </Alert>
        )}

        <Paper component="form" onSubmit={handleSubmit} sx={{ 
          p: 4, 
          mt: 2, 
          borderRadius: 4, 
          boxShadow: '0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)', 
          border: '1px solid #e2e8f0',
          background: '#ffffff'
        }}>
          <Box>
            {/* قسم النص */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <AutoAwesomeIcon sx={{ color: '#3b82f6' }} />
                <Typography variant="h6" fontWeight="600" sx={{ color: '#1e293b' }}>
                  معلومات النص
                </Typography>
                <Chip label="مطلوب" size="small" sx={{ 
                  bgcolor: '#dbeafe', 
                  color: '#1d4ed8', 
                  border: '1px solid #93c5fd',
                  fontWeight: 600
                }} />
              </Box>
              <TextField
                name="knowledge_base_text"
                fullWidth
                multiline
                rows={12}
                variant="outlined"
                value={formData.knowledge_base_text}
                onChange={handleInputChange}
                placeholder="أدخل المعلومات التي سيستخدمها الشات بوت للإجابة على أسئلة المستخدمين..."
                sx={{ 
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    '&:hover': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#3b82f6',
                      }
                    },
                    '&.Mui-focused': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#3b82f6',
                        borderWidth: '2px'
                      }
                    }
                  }
                }}
              />
              <Typography variant="caption" sx={{ color: '#64748b' }}>
                يمكنك إضافة أي معلومات مفيدة مثل: الأسئلة الشائعة، سياسات الشركة، معلومات المنتجات، إلخ.
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* قسم الملف */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <DescriptionIcon sx={{ color: '#3b82f6' }} />
                <Typography variant="h6" fontWeight="600" sx={{ color: '#1e293b' }}>
                  ملف المعرفة
                </Typography>
                <Chip label="اختياري" size="small" sx={{ 
                  bgcolor: '#f1f5f9', 
                  color: '#64748b', 
                  border: '1px solid #e2e8f0',
                  fontWeight: 600
                }} />
              </Box>
              
              <Box sx={{ 
                p: 3, 
                border: '2px dashed', 
                borderColor: '#e2e8f0', 
                borderRadius: 3,
                textAlign: 'center',
                bgcolor: '#f8fafc',
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: '#3b82f6',
                  bgcolor: '#eff6ff'
                }
              }}>
                {currentFileName ? (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ color: '#64748b' }} gutterBottom>
                      الملف الحالي:
                    </Typography>
                    <Chip 
                      label={currentFileName} 
                      sx={{ 
                        bgcolor: '#dcfce7', 
                        color: '#166534', 
                        border: '1px solid #bbf7d0',
                        fontWeight: 600
                      }}
                      icon={<DescriptionIcon />}
                    />
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
                    لا يوجد ملف مرفوع
                  </Typography>
                )}
                
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadFileIcon />}
                  sx={{ 
                    borderRadius: 3,
                    px: 3,
                    borderColor: '#3b82f6',
                    color: '#3b82f6',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)',
                      bgcolor: '#eff6ff'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  اختر ملفًا جديدًا
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.txt"
                    onChange={handleFileChange}
                  />
                </Button>
                
                {selectedFile && (
                  <Box sx={{ mt: 2 }}>
                    <Chip 
                      label={selectedFile.name} 
                      sx={{ 
                        bgcolor: '#dbeafe', 
                        color: '#1d4ed8', 
                        fontWeight: 600
                      }}
                      icon={<DescriptionIcon />}
                    />
                  </Box>
                )}
                
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 2 }}>
                  يدعم الملفات: PDF, TXT (الحد الأقصى: 10 ميجابايت)
                </Typography>
              </Box>
            </Box>
            
            <Divider sx={{ my: 3 }} />
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button 
                type="submit" 
                variant="contained" 
                disabled={saving}
                startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                sx={{ 
                  borderRadius: 3,
                  px: 4,
                  py: 1.5,
                  bgcolor: '#3b82f6',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 24px rgba(59, 130, 246, 0.3)',
                    bgcolor: '#2563eb'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                {saving ? 'جاري الحفظ...' : 'حفظ التغييرات'}
              </Button>
            </Box>
          </Box>
        </Paper>
        
        {/* رسالة تأكيد الحفظ */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={() => setSnackbarOpen(false)}
          message="تم حفظ التغييرات بنجاح!"
        />
      </Container>
    </motion.div>
  );
}
